import {
  BookOpen,
  Check,
  Images,
  KeyRound,
  Mail,
  PackageCheck,
  Smartphone,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Logo } from "@/components/ui";

import scene from "@/assets/spaza-scene.webp";
import { cn } from "@/lib/cn";

import { ApplyInvite } from "./components/ApplyInvite";
import { StokvelInvite } from "./components/StokvelInvite";
import { PasswordLoginForm } from "./components/PasswordLoginForm";
import { PinLoginForm } from "./components/PinLoginForm";

type Mode = "pin" | "password";

/**
 * Where the shopfront sits.
 *
 * Kept together, and as custom properties, because the three values are one
 * decision: the drawing has to clear the feature chips and the guide links
 * above it and stop short of the sign-in card beside it, so its height and its
 * two offsets can only be chosen against each other. The `- 6rem` is the guide
 * links' row: without it, on a 768px-high till the links sat on the awning.
 */
const SCENE = {
  "--scene-h": "clamp(28rem, calc(72vh - 6rem), 46rem)",
  "--scene-x": "clamp(0.5rem, 3.5vw, 6rem)",
  "--scene-y": "clamp(1.5rem, calc((100vh - 53.5rem) / 2), 10rem)",
  // The page is painted to match the illustration rather than the illustration
  // being altered to suit the page — which is what lets it be used exactly as
  // drawn, at full strength, with no mask and no blend mode.
  //
  // Its ground is a glow out of its own top-left corner: #02375b there, falling
  // to about #001b3b along the far edges and a shade darker again in the far
  // corner. So this is that same field — same centre, pinned to the corner with
  // the same custom properties that place the picture; an ellipse because the
  // falloff is faster vertically than horizontally; and stops read off the
  // artwork's own borders. Measured across the four edges the two sides of the
  // seam agree to within four levels out of 255, which is why there is no
  // visible frame around an image that very much has one.
  //
  // Re-fit the stops if the illustration is ever redrawn — they describe that
  // file, not a taste.
  background:
    "radial-gradient(1550px 865px at var(--scene-x) calc(100vh - var(--scene-y) - var(--scene-h))," +
    " #02375b 0%, #012d50 31%, #002b4c 39%, #001938 63%, #001b3b 84%, #011634 100%)",
} as React.CSSProperties;

/**
 * Sign in.
 *
 * Two modes, because two different people are signing in. The **PIN keypad is
 * the default**: a cashier at a till uses this screen dozens of times a week,
 * while an owner opens the back office occasionally. Optimising for the frequent
 * case is worth one extra tap for the rare one.
 *
 * The split follows the supplied design — a navy marketing panel beside a white
 * sign-in card. It earns its place on a till, where this screen is the first
 * thing a shopkeeper sees each morning and the only one that says what the
 * product is. On a phone the panel is dropped rather than stacked: somebody
 * signing in on a handset wants the keypad above the fold, not a value
 * proposition they have already bought.
 */
export function LoginPage() {
  // `?mode=password` opens on the email tab — where the set-password page sends
  // an owner who has never seen this screen and would not know to switch.
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get("mode") === "password" ? "password" : "pin");

  // The card must not jump when the tab changes. The email form is shorter
  // than the keypad, and the sign-in card is centred, so swapping one for the
  // other moved everything on the screen — the tabs a person had just tapped
  // included. On a switch the form area keeps the height it had, so the card
  // holds still; it can still grow, for a pairing screen taller than the keypad.
  //
  // Measured rather than both forms kept mounted and one hidden: the PIN form
  // listens to the whole page's keyboard for its keypad, and a hidden one would
  // swallow the digits of an email address typed into the other.
  const pane = useRef<HTMLDivElement>(null);
  const [paneHeight, setPaneHeight] = useState<number | undefined>(undefined);
  const switchTo = (next: Mode) => {
    if (next === mode) return;
    setPaneHeight(pane.current?.getBoundingClientRect().height);
    setMode(next);
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-4 sm:p-8"
      style={SCENE}
    >
      {/* ── The artwork, full-bleed ─────────────────────────────────────────
          Anchored to the bottom-left corner of the viewport rather than fitted
          to a panel: the shopfront, the basket and the scooter all stand on one
          ground line, and that line has to be the bottom of the screen or the
          scene reads as a picture of a shop instead of a street.

          Hidden below `lg`, where the card takes the whole screen and a
          background behind it would be decoration nobody can see. */}
      <img
        src={scene}
        alt=""
        aria-hidden
        className="pointer-events-none absolute hidden select-none lg:block"
        style={{
          height: "var(--scene-h)",
          left: "var(--scene-x)",
          bottom: "var(--scene-y)",
        }}
      />

      {/* ── The words ────────────────────────────────────────────────────
          Anchored to the viewport, not placed in the grid beside the card.
          They belong to the artwork below them, and the artwork is anchored to
          the bottom of the screen — so the words have to be anchored to the top
          of it, or the gap between the two is whatever the card happens to
          measure. That is not hypothetical: the card is nearly three hundred
          pixels shorter while a terminal is being paired than while somebody is
          signing in, and in the grid that difference pushed the chips down onto
          the shop's awning. */}
      <div
        // Dropped on a short window as well as a narrow one. The words sit
        // 12vh down and the shop rises 55vh from the floor, so under about
        // 640px of height there is no room between them for either to be
        // worth showing.
        className="pointer-events-none absolute inset-x-0 top-[12vh] z-10 hidden justify-center px-8 lg:flex [@media(max-height:640px)]:lg:hidden"
      >
        <div className="w-full max-w-[1200px]">
          {/* 46% rather than half: the column on the right holds the card
              and the apply panel beside it, and the words must stop short of
              both. */}
          <section className="flex w-[46%] flex-col gap-7">
            <h2
              aria-hidden
              className="relative z-10 text-[clamp(1.9rem,4.3vw,2.9rem)] font-extrabold leading-[1.07] tracking-tight text-white"
            >
              Sell smarter.
              <br />
              <span className="text-gold-500">Grow your spaza.</span>
            </h2>

            <ul aria-hidden className="relative z-10 flex flex-wrap gap-3.5">
              {[
                { icon: Smartphone, label: "POS ready" },
                { icon: Wifi, label: "Works offline" },
                { icon: PackageCheck, label: "Stock synced" },
              ].map((item) => (
                <li
                  key={item.label}
                  className="relative flex min-w-[6.75rem] flex-col items-center gap-2.5 rounded-card border border-white/15 bg-gradient-to-b from-white/[.11] to-white/[.04] px-4 py-4 shadow-pop"
                >
                  <span className="relative text-white/90">
                    <item.icon className="size-7" strokeWidth={1.6} />
                    {/* Money green, not the brand teal: a tick is a statement
                            that something works, not a piece of identity. */}
                    <Check
                      className="absolute -bottom-1.5 -right-2 size-4 rounded-full bg-money-500 p-0.5 text-white ring-[2.5px] ring-navy-800"
                      strokeWidth={3.5}
                    />
                  </span>
                  <span className="text-[0.78rem] font-semibold text-white/95">{item.label}</span>
                </li>
              ))}
            </ul>

            {/* The guides, where someone standing at an unpaired till looks
                first. The only part of this column that can be clicked. */}
            <nav
              aria-label="Guides"
              className="pointer-events-auto relative z-10 flex flex-wrap gap-3"
            >
              <GuideLink
                to="/help"
                icon={Images}
                title="Picture guide"
                detail="How do I…? Step by step"
                gold
              />
              <GuideLink
                to="/guide"
                icon={BookOpen}
                title="Shop guide"
                detail="Setting up, and training manuals"
              />
            </nav>
          </section>
        </div>
      </div>

      <div className="relative grid w-full max-w-[1200px] items-center gap-8 lg:grid-cols-[1fr_auto] lg:gap-14">
        {/* Holds the left column open for the words above. */}
        <div className="order-2 hidden lg:order-1 lg:block" aria-hidden />

        {/* ── The card ─────────────────────────────────────────────────────── */}
        <main className="order-1 flex flex-col items-center gap-4 lg:order-2 lg:flex-row lg:items-center lg:gap-5">
          {/* A fixed width beside the artwork, not "as wide as its content":
              the column it sits in is sized to fit, so the pairing screen's
              paragraph made the card wide and the email form made it narrow,
              and switching tabs resized it. */}
          <div className="w-full max-w-[27rem] rounded-[1.375rem] bg-surface p-6 shadow-pop sm:px-7 lg:w-[27rem] lg:shrink-0">
            <div className="flex justify-center">
              <Logo height={52} label="Jalusi SmartSpaza" />
            </div>

            {/* ── Mode switch ──────────────────────────────────────────────── */}
            <div
              role="tablist"
              aria-label="Sign-in method"
              className="mt-5 grid grid-cols-2 gap-1 rounded-control bg-canvas p-1"
            >
              {(
                [
                  { id: "pin", label: "Till", icon: KeyRound },
                  { id: "password", label: "Back office", icon: Mail },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={mode === tab.id}
                  onClick={() => switchTo(tab.id)}
                  className={cn(
                    "flex h-10 items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors",
                    mode === tab.id
                      ? "bg-surface text-ink-900 shadow-card"
                      : "text-ink-500 hover:text-ink-900",
                  )}
                >
                  <tab.icon className="size-4" aria-hidden />
                  {tab.label}
                </button>
              ))}
            </div>

            <div ref={pane} className="mt-4" style={{ minHeight: paneHeight }}>
              {mode === "pin" ? <PinLoginForm /> : <PasswordLoginForm />}
            </div>

            {/* The same two links, in the card, wherever the column beside it
                is not shown: a phone, a small tablet, a short window. */}
            <div className="lg:hidden [@media(max-height:640px)]:lg:block">
              <Link
                to="/help"
                className="mt-4 flex items-center justify-center gap-2 rounded-control bg-gold-50 px-3 py-2.5 font-semibold text-gold-700 hover:bg-gold-500/20"
              >
                <Images className="size-5" aria-hidden />
                How do I…? Picture guide
              </Link>
              <p className="mt-3 text-center text-sm text-ink-500">
                Setting up a till?{" "}
                <Link to="/guide" className="font-medium text-brand-500 hover:underline">
                  Read the shop guide
                </Link>
              </p>
            </div>
          </div>

          <div className="flex w-full max-w-[27rem] flex-col gap-4 lg:w-[13.5rem] lg:shrink-0">
            <ApplyInvite />
            <StokvelInvite />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * A guide link in the column over the artwork. Solid, unlike the glass chips
 * above it: the links reach down over the drawing's sky, and a cloud line
 * running through "training manuals" made them hard to read.
 */
function GuideLink({
  to,
  icon: Icon,
  title,
  detail,
  gold,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  detail: string;
  gold?: boolean;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-card border border-white/15 bg-navy-900/90 py-2.5 pl-2.5 pr-4 shadow-pop backdrop-blur-md transition-colors hover:border-white/30 hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
    >
      <span
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-control",
          gold ? "bg-gold-500 text-navy-900" : "bg-white/15 text-white",
        )}
      >
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="grid">
        <span className="font-semibold text-white">{title}</span>
        <span className="text-[0.78rem] text-white/75">{detail}</span>
      </span>
    </Link>
  );
}
